import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Stack } from "@mui/material";
import * as QrCode from "qrcode";

import { useTranslate } from "@/common/hooks/useTranslate";
import { ButtonCopy } from "@/components/ButtonCopy/ButtonCopy";
import { CloseDialogButton } from "@/components/ShareFormModal/CloseDialogButton";
import { ShareFormTabLayout } from "@/components/ShareFormModal/ShareFormTabLayout";
import { ShareFormTabTitle } from "@/components/ShareFormModal/ShareFormTabTitle";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInfobox } from "@/dashboard-design-system/Infobox";
import { ChevronLeft } from "@/icons/outlined";

type QrCodeTabProps = {
  orgPageUrl: string;
  onClose: () => void;
  onBackClick: () => void;
};

export const QrCodeTab: FC<QrCodeTabProps> = ({ orgPageUrl, onClose, onBackClick }) => {
  const { t } = useTranslate();
  const [image, setImage] = useState<{ url: string; blob: Blob } | null>(null);
  const qrCodeCanvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = qrCodeCanvasRef.current;
    if (!canvas || !orgPageUrl) {
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;
    QrCode.toCanvas(canvas, orgPageUrl).then(() => {
      if (cancelled) {
        return;
      }
      canvas.toBlob((blob) => {
        if (blob && !cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImage({ url: objectUrl, blob });
        }
      });
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [orgPageUrl]);

  const clipboardItem = useMemo(() => {
    if (image?.blob) {
      return new ClipboardItem({ "image/png": image?.blob });
    }
    return null;
  }, [image?.blob]);

  const handleOnDownload = useCallback(() => {
    if (image?.url) {
      const anchor = document.createElement("a");
      anchor.href = image.url;
      anchor.download = "org-page-qr-code.png";
      anchor.click();
    }
  }, [image?.url]);

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("organization", "shareModal.qrCode.title")}</ShareFormTabTitle>,
        content: (
          <>
            <DashboardInfobox vibe="brand" type="alert" size="medium" pointer="bottom" hideIcon>
              {t("organization", "shareModal.qrCode.infoBox")}
            </DashboardInfobox>
            <Stack alignItems="center">
              <canvas ref={qrCodeCanvasRef} width="148px" height="148px" />
              <Stack direction="row" alignItems="center" gap={1}>
                <ButtonCopy
                  loading={false}
                  vibe="brand"
                  size="large"
                  variant="outlined"
                  toCopy={clipboardItem ? [clipboardItem] : null}
                  copyText={t("common", "copyImage")}
                />
                <DashboardButton size="large" vibe="brand" variant="filled" onClick={handleOnDownload}>
                  {t("common", "downloadImage")}
                </DashboardButton>
              </Stack>
            </Stack>
          </>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
